/**
 * Schema-driven projection of a studio's typed metadata into display rows
 * (Path C Slice 6). Pure + studio-agnostic: given a {@link StudioMetadataSchema}
 * and the persisted metadata block (`asset.metadata[schema.metadataKey]`), it
 * produces label/value pairs the asset preview renders. Because it's driven by
 * the schema, it covers any studio the backend enables with no per-studio code.
 */

import type { StudioMetadataField, StudioMetadataSchema } from '../../domain';

export interface StudioMetadataRow {
  label: string;
  value: string;
}

const EMPTY = '—';

/** Format one field's raw value per its declared type. */
function formatValue(field: StudioMetadataField, raw: unknown): string {
  if (raw === undefined || raw === null || raw === '') return EMPTY;
  if (field.type === 'string[]' || Array.isArray(raw)) {
    const arr = (Array.isArray(raw) ? raw : [raw]).filter(
      v => v !== undefined && v !== null && v !== '',
    );
    return arr.length ? arr.map(String).join(', ') : EMPTY;
  }
  return String(raw);
}

/**
 * Build the ordered display rows for a studio metadata block. Returns `[]` when
 * the schema is absent or declares no fields, so the caller renders nothing.
 * The label prefers the field's human-readable `description`, falling back to
 * its `name`. A field with no value in the block renders as "—".
 */
export function studioMetadataRows(
  schema: StudioMetadataSchema | undefined,
  block: Record<string, unknown> | undefined,
): StudioMetadataRow[] {
  if (!schema?.fields?.length) return [];
  const data = block ?? {};
  return schema.fields.map(field => ({
    label: field.description?.trim() || field.name,
    value: formatValue(field, data[field.name]),
  }));
}
